import { Injectable, inject } from '@angular/core';
import {
  CoreActionNavigateToResourceStackUpdateLastItem,
  CoreActions,
  CoreMobiResourceType,
  CoreRouterActionGoBack,
  CoreRouterActionNavigateForward,
  CoreRouterActions,
  selectorCoreNavigateToResourceStackLastItemType
} from '@bepatient/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import { QuizzApiService } from './quizz-api.service';
import {
  QuizzActionLoadQuizzFailed,
  QuizzActionLoadQuizzSuccess,
  QuizzActionQuizzLeft,
  QuizzActions,
  QuizzActionSaveQuizz,
  QuizzActionSaveQuizzFailed,
  QuizzActionSaveQuizzSuccess,
  QuizzActionTypes
} from './quizz.actions';
import { selectorQuizzLeaveQuizz } from './quizz.reducer';

@Injectable()
export class QuizzEffects {
  private store$ = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private quizzApiService = inject(QuizzApiService);

  loadQuizz$: Observable<QuizzActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.LOAD_QUIZZ),
      withLatestFrom(
        this.store$.select(
          selectorCoreNavigateToResourceStackLastItemType(
            CoreMobiResourceType.QUIZZ
          )
        ),
        (action, item) => item.href
      ),
      switchMap(href =>
        this.quizzApiService.loadQuizz(href).pipe(
          map(res => new QuizzActionLoadQuizzSuccess(res)),
          catchError(() => of(new QuizzActionLoadQuizzFailed()))
        )
      )
    )
  );

  saveQuizz$: Observable<QuizzActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.SAVE_QUIZZ),
      map((action: QuizzActionSaveQuizz) => action.payload),
      withLatestFrom(
        this.store$.select(
          selectorCoreNavigateToResourceStackLastItemType(
            CoreMobiResourceType.QUIZZ
          )
        ),
        (payload, item) => [payload, item.href]
      ),
      switchMap(([payload, href]) =>
        this.quizzApiService.saveQuizz(href, payload).pipe(
          map(res => new QuizzActionSaveQuizzSuccess(res)),
          catchError(() => of(new QuizzActionSaveQuizzFailed()))
        )
      )
    )
  );

  navigateToResults$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.SAVE_QUIZZ_SUCCESS),
      map(() => new CoreRouterActionNavigateForward({ url: 'quizz-results' }))
    )
  );

  redoQuizz$: Observable<CoreActions | CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.REDO_QUIZZ),
      withLatestFrom(
        this.store$.pipe(
          select(
            selectorCoreNavigateToResourceStackLastItemType(
              CoreMobiResourceType.QUIZZ
            )
          )
        ),
        (action, item) => item
      ),
      switchMap(item => [
        new CoreActionNavigateToResourceStackUpdateLastItem({
          ...item,
          href: item.href.split('/answers/')[0]
        }),
        new CoreRouterActionGoBack()
      ])
    )
  );

  leaveQuizz$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.LEAVE_QUIZZ),
      map(() => new CoreRouterActionGoBack())
    )
  );

  shouldLeaveFromQuizz$: Observable<QuizzActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.LOAD_QUIZZ_SUCCESS),
      withLatestFrom(
        this.store$.select(selectorQuizzLeaveQuizz),
        (action, leave) => leave
      ),
      filter(leave => leave),
      map(() => new QuizzActionQuizzLeft())
    )
  );

  quizzLeft$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(QuizzActionTypes.QUIZZ_LEFT),
      map(() => new CoreRouterActionGoBack())
    )
  );
}
